import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import {
  Key, Plus, Trash2, Copy, Eye, EyeOff, Code2, Loader2,
  CheckCircle2, AlertTriangle, Zap, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

export default function LogPOSPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/log/pos/api-keys`, { headers: getHeaders() });
      setKeys(res.data.api_keys || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!newName.trim()) { toast.error("Nom requis"); return; }
    try {
      const res = await axios.post(`${API}/log/pos/api-keys`, { name: newName, description: newDesc }, { headers: getHeaders() });
      setCreatedKey(res.data.api_key);
      toast.success("Cle API creee !");
      setShowCreate(false);
      setNewName("");
      setNewDesc("");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const revokeKey = async (id) => {
    try {
      await axios.delete(`${API}/log/pos/api-keys/${id}`, { headers: getHeaders() });
      toast.success("Cle revoquee");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const copyKey = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copie !");
  };

  return (
    <LogLayout title="Integration Caisse (POS)" subtitle="Connectez votre logiciel de caisse pour automatiser les dossiers">
      <div data-testid="log-pos-page" className="space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-500/100/10 border border-blue-500/20 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-300">Comment ca marche ?</p>
              <p className="text-xs text-blue-400/70 mt-1">
                Généréz une cle API, puis configurez votre logiciel de caisse pour envoyer les bons d'assurance directement a CMES-LOG.
                Les dossiers de facturation seront créés automatiquement, sans double saisie.
              </p>
            </div>
          </div>
        </div>

        {/* Created Key Display */}
        {createdKey && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5" data-testid="pos-created-key">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-emerald-400 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Cle API creee</p>
              <button onClick={() => setCreatedKey(null)} className="text-xs text-slate-400">Fermer</button>
            </div>
            <div className="bg-slate-950 rounded-lg p-3 flex items-center gap-2">
              <code className="text-sm text-emerald-300 flex-1 break-all">{createdKey}</code>
              <button onClick={() => copyKey(createdKey)} className="p-2 hover:bg-white/5 rounded-lg text-blue-400"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Conservez cette cle, elle ne sera plus affichee.</p>
          </div>
        )}

        {/* API Keys */}
        <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-white">Cles API ({keys.filter(k => k.active).length} active{keys.filter(k => k.active).length > 1 ? "s" : ""})</h3>
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-1.5" data-testid="pos-create-key-btn">
              <Plus className="w-3.5 h-3.5" /> Nouvelle cle
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>
          ) : keys.length === 0 ? (
            <div className="text-center py-10">
              <Key className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Aucune cle API</p>
              <p className="text-slate-600 text-xs mt-1">Creez une cle pour connecter votre logiciel de caisse</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {keys.map(key => (
                <div key={key.id} data-testid={`pos-key-${key.id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02]">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${key.active ? "bg-emerald-500/15" : "bg-red-500/100/15"}`}>
                    <Key className={`w-4 h-4 ${key.active ? "text-emerald-400" : "text-red-400"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white">{key.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${key.active ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/100/20 text-red-400"}`}>
                        {key.active ? "Active" : "Révoquée"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                      <code className="text-slate-400">{key.key_prefix}</code>
                      <span>{key.usage_count} appels</span>
                      {key.last_used && <span>Dernier: {new Date(key.last_used).toLocaleDateString("fr-FR")}</span>}
                      <span>Créé: {new Date(key.created_at).toLocaleDateString("fr-FR")}</span>
                    </div>
                  </div>
                  {key.active && (
                    <button onClick={() => revokeKey(key.id)} className="p-1.5 rounded-lg hover:bg-red-500/100/100/10 text-red-400" data-testid={`revoke-key-${key.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* API Documentation */}
        <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
          <button onClick={() => setShowDocs(!showDocs)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/[0.02]" data-testid="pos-docs-toggle">
            <div className="flex items-center gap-2">
              <Code2 className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-white">Documentation API</h3>
            </div>
            <span className="text-xs text-slate-400">{showDocs ? "Masquer" : "Afficher"}</span>
          </button>
          {showDocs && (
            <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
              <DocSection title="1. Envoyer un bon d'assurance" method="POST" url="/api/log/pos/push/voucher" body={`{
  "patient_name": "Kouadio Jean",
  "insurer_name": "NSIA",
  "total_amount": 25000,
  "insured_amount": 20000,
  "patient_copay": 5000,
  "prescription_items": [
    {"name": "Amoxicilline 500mg", "qty": 21, "price": 6300}
  ],
  "prescriber_name": "Dr Bamba",
  "voucher_number": "BON-001"
}`} />
              <DocSection title="2. Envoyer un lot de bons (création auto du dossier)" method="POST" url="/api/log/pos/push/batch" body={`{
  "insurer_name": "NSIA",
  "period_start": "2026-03-01",
  "period_end": "2026-03-15",
  "vouchers": [
    {"patient_name": "Traore A.", "insurer_name": "NSIA", "total_amount": 15000, "insured_amount": 12000},
    {"patient_name": "Diallo I.", "insurer_name": "NSIA", "total_amount": 35000, "insured_amount": 28000}
  ]
}`} />
              <DocSection title="3. Verifier le statut d'une facture" method="GET" url="/api/log/pos/status/{invoice_id}" body="" />
              <div className="bg-amber-500/100/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
                Toutes les requetes doivent inclure le header: <code className="bg-slate-950 px-1.5 py-0.5 rounded">X-API-Key: votre_cle_api</code>
              </div>
            </div>
          )}
        </div>

        {/* Create Key Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="pos-create-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-sm">
              <div className="p-5 border-b border-white/5">
                <h2 className="text-base font-bold text-white">Nouvelle cle API</h2>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Nom *</label>
                  <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ex: Caisse principale" data-testid="pos-key-name"
                    className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Description</label>
                  <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optionnel" data-testid="pos-key-desc"
                    className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={createKey} className="bg-blue-600 hover:bg-blue-500/100 text-white flex-1" data-testid="pos-key-submit">Générér</Button>
                  <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10 text-slate-400">Annuler</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}

function DocSection({ title, method, url, body }) {
  const methodColor = method === "POST" ? "bg-emerald-500/20 text-emerald-400" : "bg-blue-500/100/20 text-blue-400";
  return (
    <div className="bg-slate-950 rounded-xl p-4">
      <p className="text-sm font-medium text-white mb-2">{title}</p>
      <div className="flex items-center gap-2 mb-2">
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${methodColor}`}>{method}</span>
        <code className="text-xs text-slate-300">{url}</code>
      </div>
      {body && <pre className="text-[11px] text-slate-400 bg-slate-900 rounded-lg p-3 overflow-x-auto">{body}</pre>}
    </div>
  );
}
